import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { Trash2, Plus, Edit2, Loader, EyeOff } from 'lucide-react';
import { Article } from '../../../types';
import ImageUploader from '../common/ImageUploader';

const ArticleManager: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Form State
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');
  const [type, setType] = useState<'story' | 'lookbook' | 'news'>('story');
  const [heroImage, setHeroImage] = useState('');
  const [content, setContent] = useState('');
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const q = query(collection(db, 'articles'), orderBy('publishDate', 'desc'));
      const querySnapshot = await getDocs(q);
      const list: Article[] = [];
      querySnapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Article);
      });
      setArticles(list);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setSubtitle('');
    setAuthor('');
    setType('story');
    setHeroImage('');
    setContent('');
    setVisible(true);
    setEditingId(null);
    setMessage(null);
  };

  const handleEdit = (article: Article) => {
    resetForm(); // Clear any previous state/messages
    setEditingId(article.id);
    setTitle(article.title);
    setSubtitle(article.subtitle || '');
    setAuthor(article.author);
    setType(article.type);
    setHeroImage(article.heroImage);
    setContent(article.content);
    setVisible(article.visible !== false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    try {
      await deleteDoc(doc(db, 'articles', id));
      fetchArticles();
      setMessage({ type: 'success', text: 'Article deleted successfully.' });
    } catch (error) {
      console.error("Error deleting article:", error);
      setMessage({ type: 'error', text: 'Failed to delete article.' });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !author) {
      setMessage({ type: 'error', text: 'Title and Author are required.' });
      return;
    }

    setMessage(null);
    
    try {
      const imageUrl = heroImage || '/assets/placeholder.svg';

      const articleData = {
        title,
        subtitle,
        author,
        type,
        heroImage: imageUrl,
        content: content || '...',
        visible,
        publishDate: new Date().toISOString(),
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, 'articles', editingId), articleData);
        setMessage({ type: 'success', text: 'Article updated successfully!' });
      } else {
        await addDoc(collection(db, 'articles'), {
          ...articleData,
          createdAt: serverTimestamp()
        });
        setMessage({ type: 'success', text: 'Article published successfully!' });
      }

      resetForm();
      fetchArticles();
    } catch (error: any) {
      console.error("Error saving article:", error);
      setMessage({ type: 'error', text: 'Failed to save: ' + (error.message || 'Unknown error') });
    }
  };

  if (loading) return <div className="py-8"><Loader className="animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      
      {/* List */}
      <div className="lg:col-span-2 space-y-4">
         <h2 className="font-heading font-bold text-lg mb-4">Published Articles</h2>
         
         {message && (
            <div className={`p-3 mb-4 text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {message.text}
            </div>
         )}

         {articles.length === 0 && <p className="text-gray-500 italic text-sm">No articles found.</p>}
         {articles.map(article => (
           <div key={article.id} className="bg-white border border-gray-200 p-4 flex gap-4 items-center group">
              <img src={article.heroImage} alt={article.title} className="w-20 h-20 object-cover bg-gray-100" />
              <div className="flex-1">
                <span className="text-[10px] uppercase font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded inline-block mb-1">{article.type}</span>
                <h3 className="font-heading font-bold text-lg leading-tight">{article.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{article.subtitle}</p>
                <div className="text-[10px] text-gray-400 mt-2">
                   {new Date(article.publishDate).toLocaleDateString()} • {article.author}
                   {!article.visible && <span className="ml-2 text-red-500 font-bold uppercase text-[10px] flex items-center gap-1"><EyeOff size={10}/> Hidden</span>}
                </div>
              </div>
              <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                 <button onClick={() => handleEdit(article)} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded"><Edit2 size={16} /></button>
                 <button onClick={() => handleDelete(article.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
              </div>
           </div>
         ))}
      </div>

      {/* Editor */}
      <div className="bg-gray-50 p-6 border border-gray-200 h-fit sticky top-6">
        <h2 className="font-heading font-bold text-lg mb-6 flex items-center gap-2">
           {editingId ? <Edit2 size={18}/> : <Plus size={18}/>}
           {editingId ? 'Edit Article' : 'New Article'}
        </h2>
        
        <form onSubmit={handleSave} className="space-y-4">
           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Title</label>
             <input className="w-full p-2 border border-gray-300 font-heading font-bold" value={title} onChange={e => setTitle(e.target.value)} placeholder="Article Title" required />
           </div>
           
           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Subtitle</label>
             <textarea className="w-full p-2 border border-gray-300 text-sm" rows={2} value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Short summary" />
           </div>
           
           <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Author</label>
                <input className="w-full p-2 border border-gray-300 text-sm" value={author} onChange={e => setAuthor(e.target.value)} placeholder="Writer Name" required />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Type</label>
                <select className="w-full p-2 border border-gray-300 text-sm" value={type} onChange={(e: any) => setType(e.target.value)}>
                  <option value="story">Story</option>
                  <option value="lookbook">Lookbook</option>
                  <option value="news">News</option>
                </select>
              </div>
           </div>

           <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4" checked={visible} onChange={e => setVisible(e.target.checked)} />
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500 select-none">Show on Home Page</span>
              </label>
           </div>

           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Content</label>
             <textarea className="w-full p-2 border border-gray-300 font-body text-sm h-64" value={content} onChange={e => setContent(e.target.value)} placeholder="Write your article content here..." />
           </div>

           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Hero Image</label>
             <ImageUploader
               value={heroImage}
               onChange={setHeroImage}
               pathPrefix="articles"
               aspectClass="aspect-video"
               onError={(text) => setMessage({ type: 'error', text })}
             />
           </div>

           <div className="pt-4 flex gap-2">
             <button type="submit" className="flex-1 bg-black text-white py-3 font-heading font-bold uppercase tracking-widest text-xs hover:bg-gray-800">
               {editingId ? 'Update Article' : 'Publish Article'}
             </button>
             {editingId && (
               <button type="button" onClick={resetForm} className="px-4 border border-gray-300 text-xs font-bold uppercase hover:bg-gray-100">
                 Cancel
               </button>
             )}
           </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleManager;
